import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Invoice Clients in Kenya — KRA, M-Pesa & eTIMS Guide',
      'description': 'Complete Kenyan invoicing guide: KRA PIN registration, 16% VAT threshold, KRA eTIMS electronic invoicing, M-Pesa payment details, and how to bill international clients from Kenya.',
      'url': 'https://www.invilic.com/blog/how-to-invoice-clients-kenya',
      'datePublished': '2026-06-07',
      'dateModified': '2026-06-07',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What is KRA eTIMS and do I need it as a Kenyan freelancer?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'eTIMS (Electronic Tax Invoice Management System) is the Kenya Revenue Authority system for generating and transmitting tax invoices electronically. It is mandatory for all VAT-registered businesses in Kenya. Freelancers below the VAT registration threshold (KES 5 million annual turnover) are not required to use eTIMS, though they may register voluntarily. For non-VAT invoices, a standard invoice without eTIMS is valid.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the VAT rate in Kenya?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'The standard VAT rate in Kenya is 16%. VAT registration is mandatory for businesses with annual taxable turnover exceeding KES 5 million. Export of services to non-resident clients is zero-rated at 0% VAT — Kenyan freelancers billing US, UK, or EU clients do not add 16% VAT on such invoices.' },
        },
        {
          '@type': 'Question',
          'name': 'Can I include M-Pesa details on a Kenyan invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, and for local Kenyan clients, M-Pesa is one of the most practical payment methods. Add your M-Pesa number (or Lipa na M-Pesa Paybill/till number for businesses) in the payment details section of your invoice. For corporate clients handling larger amounts, include both M-Pesa and bank account details.' },
        },
        {
          '@type': 'Question',
          'name': 'How do Kenyan freelancers receive international payments?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'The most widely used methods are: Payoneer (USD virtual account, withdraw to Kenyan bank or M-Pesa), Wise (EUR/GBP/USD to Kenyan bank account), direct SWIFT wire transfer to KCB, Equity Bank, or Co-op Bank, and WorldRemit or Remitly for smaller amounts. Invoice in USD for US clients and GBP for UK clients.' },
        },
        {
          '@type': 'Question',
          'name': 'What is a KRA PIN and how do I get one?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A KRA PIN (Personal Identification Number) is your tax identity number issued by the Kenya Revenue Authority. It is required for any business activity in Kenya and for filing tax returns. Register online at itax.kra.go.ke — PIN registration is free and typically takes about 15 minutes. You will need a national ID and your personal details.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const invoiceFields = [
  { field: 'Your full name or business name', note: 'Use the name registered with KRA or your trading name' },
  { field: 'Your KRA PIN', note: 'Required for VAT-registered businesses; recommended for all invoices' },
  { field: 'Your business address and contact', note: 'Physical address, email, and phone number' },
  { field: "Client's name and KRA PIN (if applicable)", note: 'For B2B invoices, client KRA PIN may be required by their accounts department' },
  { field: 'A unique sequential invoice number', note: 'e.g. INV-2026-001 — never reuse or skip numbers' },
  { field: 'Invoice date and payment due date', note: 'Net 14 or Net 30 are standard in Kenya' },
  { field: 'Description of goods or services', note: 'Quantity, rate, and description per line item' },
  { field: 'Amount in KES or USD', note: 'Invoice in USD for international clients; KES for local clients' },
  { field: 'VAT breakdown (if VAT registered)', note: 'Net amount + 16% VAT + gross total — three separate lines' },
  { field: 'Payment details', note: 'Bank account number, M-Pesa number, or Lipa na M-Pesa Paybill/till' },
];

export default function HowToInvoiceClientsKenyaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Invoice Clients in Kenya — KRA, M-Pesa & eTIMS Guide | Invilic Blog"
        description="Kenyan invoicing guide: KRA PIN registration, 16% VAT threshold, eTIMS electronic invoicing, M-Pesa payment details, and billing international clients from Kenya."
        canonical="https://www.invilic.com/blog/how-to-invoice-clients-kenya"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">International</span>
          </div>
          <span className="inline-block bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">International</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            How to Invoice Clients in Kenya — KRA, M-Pesa & eTIMS Guide
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">June 7, 2026 · 6 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            Whether you are a Kenyan freelancer billing an international client in USD or a local business invoicing in KES, the rules are straightforward once you understand the KRA PIN, the 16% VAT threshold, how eTIMS fits in, and how to include M-Pesa as a payment option.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What to Include on a Kenyan Invoice</h2>
          <p className="mb-4">A well-structured Kenyan invoice should contain all of these fields:</p>
          <div className="space-y-3 mb-8">
            {invoiceFields.map(({ field, note }, i) => (
              <div key={field} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 flex items-center justify-center text-xs font-bold mt-0.5">{i + 1}</div>
                <div className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{field}</span>
                  {note && <p className="text-gray-400 dark:text-gray-500 mt-0.5">{note}</p>}
                </div>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Create a professional invoice in KES, USD, or any currency — includes all Kenyan invoice fields. Free, no account needed."
            to="/invoice-generator-kenya"
            btnText="Create Kenyan Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Kenya VAT — The 16% Rate and KES 5 Million Threshold</h2>
          <p className="mb-4">VAT in Kenya is governed by the Value Added Tax Act, administered by the Kenya Revenue Authority (KRA):</p>
          <div className="space-y-3 mb-6">
            {[
              { heading: 'Standard rate: 16%', body: 'Applies to most goods and services supplied within Kenya. Charged on the VAT-exclusive (net) price.' },
              { heading: 'Registration threshold: KES 5 million/year', body: 'You must register for VAT once your annual taxable turnover exceeds KES 5 million. Voluntary registration is available below this threshold.' },
              { heading: 'Export of services: zero-rated (0%)', body: 'Services supplied to non-resident foreign clients are zero-rated. Kenyan freelancers billing US, UK, or EU clients do not add 16% VAT on those invoices.' },
              { heading: 'Exempt supplies', body: 'Some goods and services are VAT-exempt (e.g., basic foodstuffs, financial services, educational services). Check the VAT Act schedules or consult a tax advisor.' },
            ].map(({ heading, body }) => (
              <div key={heading} className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl text-sm">
                <p className="font-semibold text-gray-900 dark:text-white mb-1">{heading}</p>
                <p className="text-gray-500 dark:text-gray-400">{body}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">KRA eTIMS — Electronic Tax Invoicing</h2>
          <p className="mb-4">
            eTIMS (Electronic Tax Invoice Management System) is KRA's platform for generating, transmitting, and storing tax invoices digitally. It replaces the old Electronic Tax Register (ETR) machines.
          </p>
          <div className="space-y-3 mb-8">
            {[
              { label: 'Who must use eTIMS', detail: 'All VAT-registered businesses in Kenya are required to use eTIMS for issuing tax invoices. Non-compliance attracts penalties from KRA.' },
              { label: 'Who does not need eTIMS', detail: 'Freelancers and businesses below the KES 5 million VAT threshold issuing standard non-VAT invoices are not required to use eTIMS.' },
              { label: 'How to access eTIMS', detail: 'Register at etims.kra.go.ke using your KRA PIN. Access via the web portal, eTIMS Lite mobile app, or API integration for high-volume businesses.' },
              { label: 'What eTIMS does', detail: 'eTIMS generates a unique invoice number, records the transaction in real time with KRA, and creates a compliant tax invoice with a QR code for verification.' },
            ].map(({ label, detail }) => (
              <div key={label} className="border border-gray-100 dark:border-gray-800 rounded-xl p-4 text-sm">
                <p className="font-bold text-gray-900 dark:text-white mb-1">{label}</p>
                <p className="text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Adding M-Pesa to Your Invoice</h2>
          <p className="mb-4">
            M-Pesa is the dominant payment method in Kenya for local transactions. Adding your M-Pesa details removes friction for local clients:
          </p>
          <ul className="space-y-2 mb-8 text-sm">
            {[
              'Individual freelancers: add your M-Pesa mobile number (Safaricom number) in the payment details section',
              'Small businesses: register a Lipa na M-Pesa Paybill number — clients pay without needing your personal number',
              'Businesses with physical locations: use an M-Pesa Till Number for in-person buy goods payments',
              'For amounts above KES 150,000, corporate clients may prefer bank transfer — include both options',
              'Always confirm the exact amount and number before sending — M-Pesa transfers are instant and hard to reverse',
            ].map(item => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Receiving International Payments in Kenya</h2>
          <p className="mb-4">If billing clients overseas, here are the most reliable options for Kenyan freelancers:</p>
          <div className="space-y-3 mb-8">
            {[
              { method: 'Payoneer', detail: 'Virtual USD/GBP/EUR account. Withdraw to Kenyan bank account (KCB, Equity Bank, etc.) or directly to M-Pesa. Widely accepted by international clients and marketplaces.' },
              { method: 'Wise', detail: 'Excellent exchange rates with transparent fees. Receive in USD, GBP, or EUR and withdraw to your Kenyan bank. Fast and reliable for regular payments.' },
              { method: 'SWIFT wire transfer', detail: 'Direct transfer to your Kenyan bank USD domiciliary account. Best for large amounts. Sender fees are typically $15–50 per transfer.' },
              { method: 'WorldRemit / Remitly', detail: 'Good for receiving smaller payments from UK, US, or Canadian clients — can deliver directly to M-Pesa within minutes.' },
            ].map(({ method, detail }) => (
              <div key={method} className="border border-gray-100 dark:border-gray-800 rounded-xl p-4 text-sm">
                <p className="font-bold text-gray-900 dark:text-white mb-1">{method}</p>
                <p className="text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a Kenyan invoice in KES or USD — M-Pesa fields, KRA PIN, 16% VAT calculation. Free, no account needed."
            to="/invoice-generator-kenya"
            btnText="Create Kenyan Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/how-to-invoice-clients-kenya" />
      </main>
    </div>
  );
}
